import React, { useState, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { AIBlockService } from "frontend/services/ai-blocks/ai-block-service";
import { textShortenerBlock } from "frontend/services/ai-blocks/edit-blocks/text-shortener-block";
import { getElement } from "shared/datamodel/canvas-element";
import { undoRedoStackAtom } from "state-atoms/stage-atoms";
import { syncServiceAtom } from "state-atoms/general-atoms";
import style from "./ai-edits-panel.module.css";
import { Icon } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";

interface ShortenTextButtonProps {
  selectedElementIds: string[];
  onClose?: () => void;
  onError?: (error: string) => void;
}

export function ShortenTextButton({
  selectedElementIds,
  onClose,
  onError,
}: ShortenTextButtonProps): React.ReactElement {
  const [isProcessing, setIsProcessing] = useState(false);

  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const syncService = useAtomValue(syncServiceAtom);

  const aiBlockService = AIBlockService.getInstance();

  useEffect(() => {
    aiBlockService.registerBlock("text-shortener", textShortenerBlock);
  }, []);

  const handleShortenText = useCallback(async () => {
    if (!syncService || selectedElementIds.length === 0) return;

    setIsProcessing(true);

    try {
      const reflect = syncService.getReplicache();
      if (!reflect) {
        onError?.("Canvas not ready");
        return;
      }

      const elementsData = await reflect.query(async (tx) => {
        const elements = await Promise.all(
          selectedElementIds.map(async (id) => {
            const element = await getElement(tx, id);
            return { id, element };
          })
        );
        return elements;
      });

      const { edits } = await aiBlockService.generateEdits("text-shortener", {
        prompt: "Shorten the following texts",
        elementIds: elementsData.map((e) => e.id),
        texts: elementsData.map((e) => (e.element as { text?: string }).text || ""),
      });

      const editsMap = new Map(edits.map((e) => [e.elementId, e]));
      const idsToUpdate = selectedElementIds.filter((id) => editsMap.has(id));

      let elementIndex = 0;
      await undoRedoStack?.patchElements?.(
        idsToUpdate,
        (element) => {
          const elementId = idsToUpdate[elementIndex];
          const edit = editsMap.get(elementId);
          if (edit) {
            Object.assign(element, edit.patch);
          }
          elementIndex += 1;
        },
        `shorten-text-${Date.now()}`
      );

      onClose?.();
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to shorten text";
      onError?.(errorMessage);
      console.error("Error shortening text:", error_);
    } finally {
      setIsProcessing(false);
    }
  }, [syncService, selectedElementIds, aiBlockService, undoRedoStack, onClose, onError]);

  const isDisabled = selectedElementIds.length === 0 || isProcessing;

  return (
    <button
      className={style.actionButton}
      title="Make your text more concise"
      onClick={handleShortenText}
      disabled={isDisabled}
    >
      <Icon icon="shorten-text" />
      {isProcessing ? "Processing..." : "Shorten Text"}
    </button>
  );
}
